import { Suspense, lazy } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { ApprovalStatus, UserRole, type AuthUser } from '@musicville/shared';
import { IconSprite } from '@/components/brand/Icon';
import { ScrollToTop } from '@/components/layout/PageShell';
import { Skeleton } from '@/components/ui/primitives';
import { useAuth } from '@/lib/auth';
import { isAdminRole } from '@/lib/admin-nav';
import { isTeacherRole } from '@/lib/portal-nav';

import HomePage from '@/routes/HomePage';
import NotFoundPage from '@/routes/NotFoundPage';

const TeachersPage = lazy(() => import('@/routes/TeachersPage'));
const TeacherProfilePage = lazy(() => import('@/routes/TeacherProfilePage'));
const LessonsPage = lazy(() => import('@/routes/LessonsPage'));
const SubjectPage = lazy(() => import('@/routes/SubjectPage'));
const PracticeRoomsPage = lazy(() => import('@/routes/PracticeRoomsPage'));
const RoomDetailPage = lazy(() => import('@/routes/RoomDetailPage'));
const RoomBookingPage = lazy(() => import('@/routes/RoomBookingPage'));
const RoomBookingConfirmation = lazy(() => import('@/routes/RoomBookingConfirmation'));
const AboutPage = lazy(() => import('@/routes/AboutPage'));
const ContactPage = lazy(() => import('@/routes/ContactPage'));
const CommunityPage = lazy(() => import('@/routes/CommunityPage'));
const FaqPage = lazy(() => import('@/routes/FaqPage'));
const BookingPolicyPage = lazy(() =>
  import('@/routes/PolicyPage').then((m) => ({ default: m.BookingPolicyPage })),
);
const PrivacyPage = lazy(() =>
  import('@/routes/PolicyPage').then((m) => ({ default: m.PrivacyPage })),
);

const DashboardPage = lazy(() => import('@/routes/portal/DashboardPage'));
const SchedulePage = lazy(() => import('@/routes/portal/SchedulePage'));
const LessonDetailDialog = lazy(() => import('@/routes/portal/LessonDetailDialog'));
const ExplorePage = lazy(() => import('@/routes/portal/ExplorePage'));
const PortalRoomsPage = lazy(() => import('@/routes/portal/PortalRoomsPage'));
const PaymentsPage = lazy(() => import('@/routes/portal/PaymentsPage'));
const PaymentDetailPage = lazy(() => import('@/routes/portal/PaymentDetailPage'));
const PortalCommunityPage = lazy(() => import('@/routes/portal/PortalCommunityPage'));
const QuestionDetailPage = lazy(() => import('@/routes/portal/QuestionDetailPage'));
const AskQuestionPage = lazy(() => import('@/routes/portal/AskQuestionPage'));
const NotificationsPage = lazy(() => import('@/routes/portal/NotificationsPage'));
const ProfilePage = lazy(() => import('@/routes/portal/ProfilePage'));

const TeacherDashboardPage = lazy(() => import('@/routes/teacher/DashboardPage'));
const TeacherSchedulePage = lazy(() => import('@/routes/teacher/SchedulePage'));
const TeacherLessonDetailDialog = lazy(() => import('@/routes/teacher/LessonDetailDialog'));
const TeacherAttendancePage = lazy(() => import('@/routes/teacher/AttendancePage'));
const TeacherStudentsPage = lazy(() => import('@/routes/teacher/StudentsPage'));
const TeacherCommunityPage = lazy(() => import('@/routes/teacher/CommunityPage'));
const TeacherPortalProfilePage = lazy(() => import('@/routes/teacher/ProfilePage'));

const AdminDashboardPage = lazy(() => import('@/routes/admin/DashboardPage'));
const AdminBookingsPage = lazy(() => import('@/routes/admin/BookingsPage'));
const AdminBookingDetailPage = lazy(() => import('@/routes/admin/BookingDetailPage'));
const AdminSchedulePage = lazy(() => import('@/routes/admin/SchedulePage'));
const AdminTeachersPage = lazy(() => import('@/routes/admin/TeachersPage'));
const AdminTeacherDetailPage = lazy(() => import('@/routes/admin/TeacherDetailPage'));
const AdminStudentsPage = lazy(() => import('@/routes/admin/StudentsPage'));
const AdminRoomsPage = lazy(() => import('@/routes/admin/RoomsPage'));
const AdminSubjectsPage = lazy(() => import('@/routes/admin/SubjectsPage'));
const AdminPaymentsPage = lazy(() => import('@/routes/admin/PaymentsPage'));
const AdminContentPage = lazy(() => import('@/routes/admin/ContentPage'));
const AdminInboxPage = lazy(() => import('@/routes/admin/InboxPage'));
const AdminSettingsPage = lazy(() => import('@/routes/admin/SettingsPage'));
const AdminStaffSignInPage = lazy(() => import('@/routes/admin/StaffSignInPage'));

const Step1Subject = lazy(() => import('@/routes/booking/Step1Subject'));
const Step2Teacher = lazy(() => import('@/routes/booking/Step2Teacher'));
const Step3Time = lazy(() => import('@/routes/booking/Step3Time'));
const Step4Details = lazy(() => import('@/routes/booking/Step4Details'));
const Step5Review = lazy(() => import('@/routes/booking/Step5Review'));
const BookingSuccess = lazy(() => import('@/routes/booking/BookingSuccess'));
const BookingError = lazy(() => import('@/routes/booking/BookingError'));

const LogInPage = lazy(() => import('@/routes/auth/LogInPage'));
const SignUpPage = lazy(() => import('@/routes/auth/SignUpPage'));
const ForgotPasswordPage = lazy(() => import('@/routes/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/routes/auth/ResetPasswordPage'));
const VerifyAccountPage = lazy(() => import('@/routes/auth/VerifyAccountPage'));
const AccountPendingPage = lazy(() => import('@/routes/auth/AccountPendingPage'));

export default function App(): React.JSX.Element {
  return (
    <>
      <IconSprite />
      <ScrollToTop />

      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />

          <Route path="/lessons" element={<LessonsPage />} />
          <Route path="/lessons/:slug" element={<SubjectPage />} />
          <Route path="/teachers" element={<TeachersPage />} />
          <Route path="/teachers/:slug" element={<TeacherProfilePage />} />

          <Route path="/practice-rooms" element={<PracticeRoomsPage />} />
          <Route path="/practice-rooms/booking/:reference" element={<RoomBookingConfirmation />} />
          <Route path="/practice-rooms/:slug" element={<RoomDetailPage />} />
          <Route path="/practice-rooms/:slug/book" element={<RoomBookingPage />} />

          <Route path="/community" element={<CommunityPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/booking-policy" element={<BookingPolicyPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          <Route path="/booking" element={<Navigate to="/booking/subject" replace />} />
          <Route path="/booking/subject" element={<Step1Subject />} />
          <Route path="/booking/teacher" element={<Step2Teacher />} />
          <Route path="/booking/time" element={<Step3Time />} />
          <Route path="/booking/details" element={<Step4Details />} />
          <Route path="/booking/review" element={<Step5Review />} />
          <Route path="/booking/success/:reference" element={<BookingSuccess />} />
          <Route path="/booking/error" element={<BookingError />} />

          <Route path="/auth/log-in" element={<LogInPage />} />
          <Route path="/auth/sign-up" element={<SignUpPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/verify" element={<VerifyAccountPage />} />

          <Route path="/account" element={<Navigate to="/portal" replace />} />

          <Route
            path="/portal"
            element={
              <RequireAuth>
                <Outlet />
              </RequireAuth>
            }
          >
            <Route
              index
              element={<ByRole teacher={<TeacherDashboardPage />} student={<DashboardPage />} />}
            />

            <Route
              path="schedule"
              element={<ByRole teacher={<TeacherSchedulePage />} student={<SchedulePage />} />}
            >
              <Route
                path="lesson/:reference"
                element={
                  <ByRole
                    teacher={<TeacherLessonDetailDialog />}
                    student={<LessonDetailDialog kind="lesson" />}
                  />
                }
              />
              <Route path="room/:reference" element={<LessonDetailDialog kind="room" />} />
            </Route>

            <Route path="explore" element={<ExplorePage />} />
            <Route path="rooms" element={<PortalRoomsPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="payments/:invoiceNo" element={<PaymentDetailPage />} />

            <Route
              path="students"
              element={
                <RequireTeacher>
                  <TeacherStudentsPage />
                </RequireTeacher>
              }
            />
            <Route
              path="attendance"
              element={
                <RequireTeacher>
                  <TeacherAttendancePage />
                </RequireTeacher>
              }
            />

            <Route
              path="community"
              element={
                <ByRole teacher={<TeacherCommunityPage />} student={<PortalCommunityPage />} />
              }
            />
            <Route path="community/ask" element={<AskQuestionPage />} />
            <Route path="community/:slug" element={<QuestionDetailPage />} />

            <Route path="notifications" element={<NotificationsPage />} />
            <Route
              path="profile"
              element={<ByRole teacher={<TeacherPortalProfilePage />} student={<ProfilePage />} />}
            />
          </Route>

          <Route path="/admin/sign-in" element={<AdminStaffSignInPage />} />

          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <Outlet />
              </RequireAdmin>
            }
          >
            <Route index element={<AdminDashboardPage />} />

            <Route path="bookings" element={<AdminBookingsPage />} />
            <Route path="bookings/:reference" element={<AdminBookingDetailPage />} />

            <Route path="schedule" element={<AdminSchedulePage />} />

            <Route path="teachers" element={<AdminTeachersPage />} />
            <Route path="teachers/:slug" element={<AdminTeacherDetailPage />} />

            <Route path="students" element={<AdminStudentsPage />} />
            <Route path="rooms" element={<AdminRoomsPage />} />
            <Route path="subjects" element={<AdminSubjectsPage />} />
            <Route path="payments" element={<AdminPaymentsPage />} />
            <Route path="content" element={<AdminContentPage />} />
            <Route path="inbox" element={<AdminInboxPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <RouteFallback />;

  if (!user) {
    return <Navigate to="/auth/log-in" replace state={{ from: location.pathname }} />;
  }

  if (isAwaitingApproval(user)) return <AccountPendingPage />;

  return <>{children}</>;
}

function isAwaitingApproval(user: AuthUser): boolean {
  return user.role === UserRole.STUDENT && user.approvalStatus !== ApprovalStatus.APPROVED;
}

function ByRole({
  teacher,
  student,
}: {
  teacher: React.ReactNode;
  student: React.ReactNode;
}): React.JSX.Element {
  const { user } = useAuth();
  return <>{isTeacherRole(user?.role) ? teacher : student}</>;
}

function RequireTeacher({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { user, isLoading } = useAuth();

  if (isLoading) return <RouteFallback />;
  if (!isTeacherRole(user?.role)) return <Navigate to="/portal" replace />;

  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <RouteFallback />;

  if (!user) {
    return <Navigate to="/admin/sign-in" replace state={{ from: location.pathname }} />;
  }
  if (!isAdminRole(user.role)) return <Navigate to="/portal" replace />;

  return <>{children}</>;
}

function RouteFallback(): React.JSX.Element {
  return (
    <div className="min-h-screen bg-canvas">
      <div className="h-22 bg-midnight" />
      <div className="mv-container flex flex-col gap-6 py-20">
        <Skeleton className="h-12 w-1/2" />
        <Skeleton className="h-72 w-full rounded-lg" />
      </div>
    </div>
  );
}
